import { socketService } from './socket';
import { store } from '../store';
import { addNotification } from '../store/slices/notificationSlice';
import { updateChatStatus, addMessage, addChatRequest, incrementUnreadCount } from '../store/slices/chatSlice';
import { setLastCheckIn } from '../store/slices/authSlice';

/**
 * Initialize all WebSocket event listeners
 */
export function initializeWebSocketEvents() {
  const socket = socketService.getSocket();
  if (!socket) return;

  // Chat events
  socket.on('chatRequest', (data: any) => {
    const user = store.getState().auth.user;
    // Add to pending requests in chat slice
    store.dispatch(addChatRequest({
      Id: data.id || data.Id,
      id: data.id || data.Id,
      Reason: data.reason || data.message || data.subject,
      reason: data.reason || data.message || data.subject,
      UserId: data.userId,
      UserName: data.userName,
      UserEmail: data.userEmail,
      CompanyName: data.companyName,
      CreatedAt: data.created || new Date().toISOString()
    } as any));
    
    // Also add notification
    store.dispatch(addNotification({
      id: Date.now().toString(),
      userId: user?.id || '',
      title: 'New Chat Request',
      body: `${data.userName || 'Customer'} is requesting support`,
      priority: 'high',
      createdAt: new Date(),
      data
    }));
  });

  socket.on('chatContent', (data: any) => {
    // Handle chat content from check-in
    if (data.chatId && data.messages) {
      data.messages.forEach((msg: any) => {
        store.dispatch(addMessage({ 
          chatId: data.chatId, 
          message: {
            id: msg.id,
            chatId: data.chatId,
            senderId: msg.senderId,
            content: msg.content,
            timestamp: msg.timestamp,
            status: 'delivered',
            encrypted: false,
            isFromCurrentUser: msg.isFromCurrentUser // Add flag for message side detection
          }
        }));
      });
    }
  });

  // New chat message received
  socket.on('newChatMessage', (data: any) => {
    const currentUser = store.getState().auth.user;
    const activeChat = store.getState().chat.activeChat;
    
    // Add message to store
    store.dispatch(addMessage({
      chatId: data.chat_id || data.chatId,
      message: {
        id: data.id,
        chatId: data.chat_id || data.chatId,
        senderId: data.participant_id || data.senderId,
        senderName: data.senderName,
        content: data.message || data.content,
        timestamp: data.created || data.timestamp,
        status: 'delivered',
        encrypted: false
      } as any
    }));
    
    // Increment unread if not from current user and not in active chat
    if (data.participant_id !== currentUser?.loginId && 
        (data.chat_id || data.chatId) !== activeChat) {
      store.dispatch(incrementUnreadCount());
    }
  });

  // Chat message from legacy system
  socket.on('chatMessage', (data: any) => {
    store.dispatch(addMessage({
      chatId: data.chatId,
      message: {
        id: data.id,
        chatId: data.chatId,
        senderId: data.senderId,
        senderName: data.senderName,
        content: data.content,
        timestamp: data.timestamp,
        status: 'sent',
        encrypted: data.encrypted || false
      } as any
    }));
  });

  // Push notifications
  socket.on('pushNotification', (data: any) => {
    const user = store.getState().auth.user;
    store.dispatch(addNotification({
      id: data.id,
      userId: user?.id || '',
      title: data.title,
      body: data.body,
      priority: data.priority || 'normal',
      createdAt: new Date(data.createdAt),
      data: data.data
    }));

    // Show browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(data.title, {
        body: data.body,
        icon: '/logo192.png',
        tag: data.id
      });
    }
  });

  // Meeting events
  socket.on('meetingCreated', (data: any) => {
    const user = store.getState().auth.user;
    store.dispatch(addNotification({
      id: Date.now().toString(),
      userId: user?.id || '',
      title: 'Meeting Created',
      body: `Meeting "${data.subject}" scheduled`,
      priority: 'normal',
      createdAt: new Date(),
      data
    }));
  });

  socket.on('meetingResponseReceived', (data: any) => {
    const user = store.getState().auth.user;
    store.dispatch(addNotification({
      id: Date.now().toString(),
      userId: user?.id || '',
      title: 'Meeting Response',
      body: `${data.responderId} ${data.response}ed the meeting`,
      priority: 'normal',
      createdAt: new Date(),
      data
    }));
  });

  // Secure message events
  socket.on('secureMessageSent', (data: any) => {
    const user = store.getState().auth.user;
    store.dispatch(addNotification({
      id: Date.now().toString(),
      userId: user?.id || '',
      title: 'Secure Message Sent',
      body: `Message sent to ${data.recipientId}. Code: ${data.code}`,
      priority: 'normal',
      createdAt: new Date(),
      data
    }));
  });

  socket.on('secureMessageReceived', (data: any) => {
    const user = store.getState().auth.user;
    store.dispatch(addNotification({
      id: data.id,
      userId: user?.id || '',
      title: 'Secure Message Received',
      body: `New secure message from ${data.senderName}`,
      priority: 'high',
      createdAt: new Date(data.createdAt),
      data
    }));
  });

  socket.on('secureMessageStatusUpdate', (data: any) => {
    const status = data.delivered ? 'delivered' : 
                  data.read ? 'read' : 
                  data.expired ? 'expired' : 'pending';
    const user = store.getState().auth.user;
    
    store.dispatch(addNotification({
      id: Date.now().toString(),
      userId: user?.id || '',
      title: 'Message Status Update',
      body: `Message ${data.messageId} is ${status}`,
      priority: 'low',
      createdAt: new Date(),
      data
    }));
  });

  // Caller ID verification events
  socket.on('callerIdVerified', (data: any) => {
    const user = store.getState().auth.user;
    store.dispatch(addNotification({
      id: Date.now().toString(),
      userId: user?.id || '',
      title: 'Caller ID Verified',
      body: `Phone ${data.phoneNumber} verified`,
      priority: 'normal',
      createdAt: new Date(),
      data
    }));
  });

  socket.on('callerIdVerificationStatus', (data: any) => {
    const user = store.getState().auth.user;
    store.dispatch(addNotification({
      id: Date.now().toString(),
      userId: user?.id || '',
      title: 'Verification Status',
      body: data.message || `Verification ${data.status}`,
      priority: data.expired ? 'high' : 'normal',
      createdAt: new Date(),
      data
    }));
  });

  // Sync complete event
  socket.on('syncComplete', (data: any) => {
    // Update any UI elements that depend on sync
    // Removed console.log to prevent memory issues from frequent logging
  });

  // Config update event
  socket.on('configUpdate', (data: any) => {
    // Update configuration in store if needed
    // Removed console.log to prevent memory issues from frequent logging
  });

  // Check-in update event
  socket.on('checkInUpdate', (data: any) => {
    // Update last check-in timestamp
    store.dispatch(setLastCheckIn(data.timestamp || new Date().toISOString()));
    // Removed console.log to prevent memory issues from frequent logging
  });
}

/**
 * Clean up all WebSocket event listeners
 */
export function cleanupWebSocketEvents() {
  const socket = socketService.getSocket();
  if (!socket) return;

  // Remove all custom event listeners
  socket.off('chatRequest');
  socket.off('chatContent');
  socket.off('chatMessage');
  socket.off('newChatMessage');
  socket.off('pushNotification');
  socket.off('meetingCreated');
  socket.off('meetingResponseReceived');
  socket.off('secureMessageSent');
  socket.off('secureMessageReceived');
  socket.off('secureMessageStatusUpdate');
  socket.off('callerIdVerified');
  socket.off('callerIdVerificationStatus');
  socket.off('syncComplete');
  socket.off('configUpdate');
  socket.off('checkInUpdate');
}